import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Grid,
  Typography,
  Button,
  Divider,
  Box,
  Alert,
  Snackbar,
} from "@mui/material";
import PositiveIntegerInput from "./PositiveIntegerInput";
import TimeLengthInput from "./TimeLengthInput";
import MultipleChoice from "./MultipleChoice";
import FreeResponse from "./FreeResponse";
import RatingSelector from "./RatingSelector";
import { useViewMode } from "../contexts/ViewModeContext";
import { isStringInstrument } from "../lib/definitions/InstrumentTrackAnnotation";
import "./AnnotationForm.css";
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';

// Prefixes for fields allowing multiple answers
const EXTENDED_PREFIXES = [
  "artist",
  "lyricist",
  "songwriter",
  "arranger",
  "composer",
  "emotion",
  "imagery",
  "music_style_description",
  "professional_description",
  "casual_description",
  "harmonic_progression_verbal_description",
  "section_description",
  "rhythm description",
  "articulation techniques",
  "effects of articulation",
  "melody description",
];

const parseTimeLength = value => {
  if (typeof value !== "string") return null;
  const [minutes, seconds] = value.split(":");
  const parsedMinutes = parseInt(minutes, 10);
  const parsedSeconds = parseInt(seconds, 10);
  return isNaN(parsedMinutes) || isNaN(parsedSeconds)
    ? null
    : { minutes: parsedMinutes, seconds: parsedSeconds };
};

const cloneFormValues = values => Object.entries(values || {}).reduce((copy, [key, value]) => {
  copy[key] = Array.isArray(value)
    ? [...value]
    : value && typeof value === 'object'
      ? { ...value }
      : value;
  return copy;
}, {});

const normalizeInitialValues = values => {
  const normalized = cloneFormValues(values);
  if (typeof normalized.duration_time_length === "string") {
    normalized.duration_time_length = parseTimeLength(normalized.duration_time_length);
  }
  return normalized;
};

//allowMultipleAnswers
export default function AnnotationForm({
  attributes,
  initialValues = {},
  onChange = () => {},
  onSubmit = () => {},
  onSubmissionComplete = () => {},
  trackName,
  ownerId,
}) {
  const { isViewMode } = useViewMode();

  const [selectedValues, setSelectedValues] = useState(() => normalizeInitialValues(initialValues));
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [baselineValues, setBaselineValues] = useState(() => normalizeInitialValues(initialValues));
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const onSubmitRef = useRef(onSubmit);
  const onSubmissionCompleteRef = useRef(onSubmissionComplete);
  const pendingLegacyValuesRef = useRef(null);
  const submittedOwnerIdRef = useRef(undefined);
  const initializedOwnerIdRef = useRef(ownerId);
  onSubmitRef.current = onSubmit;
  onSubmissionCompleteRef.current = onSubmissionComplete;

  const filteredAttributes = useMemo(() => {
    if (!attributes) return {};
    const result = {};
    Object.entries(attributes).forEach(([cat, fields]) => {
      const keep = fields.filter(f => f.name !== "duration_number_of_bars");
      if (keep.length) result[cat] = keep;
    });
    return result;
  }, [attributes]);

  useEffect(() => {
    if (ownerId !== undefined && initializedOwnerIdRef.current === ownerId) return;

    initializedOwnerIdRef.current = ownerId;
    const normalized = normalizeInitialValues(initialValues);
    setSelectedValues(normalized);
    setBaselineValues(cloneFormValues(normalized));
  }, [initialValues, ownerId]);

  const isFieldValid = (name, type, values = selectedValues) => {
    const val = values[name];
    if (type === "positive-integer") return Number.isInteger(val) && val >= 0;
    if (type === "time-length") return val != null;
    if (type === "rating") return Number.isInteger(val) && val >= 1 && val <= 5;
    if (Array.isArray(val)) return val.length > 0 && val.every(v => v.trim().length > 0);
    return typeof val === "string" && val.trim().length > 0;
  };

  useEffect(() => {
    const valid = Object.values(filteredAttributes).flat().every(field =>
      !field.mandatory || isFieldValid(field.name, field.type)
    );
    setIsFormValid(valid);
  }, [selectedValues, filteredAttributes]);

  const completeSubmissionFeedback = useCallback(() => {
    setSnackbarOpen(false);
    if (submittedOwnerIdRef.current !== undefined) {
      const submittedOwnerId = submittedOwnerIdRef.current;
      submittedOwnerIdRef.current = undefined;
      onSubmissionCompleteRef.current(submittedOwnerId);
    } else if (pendingLegacyValuesRef.current) {
      const pendingValues = pendingLegacyValuesRef.current;
      pendingLegacyValuesRef.current = null;
      onSubmitRef.current(pendingValues);
    }
  }, []);

  // Block submission is immediate; this timer is visual-only. Forms without
  // a block owner retain their existing delayed one-argument submit contract.
  useEffect(() => {
    if (snackbarOpen) {
      const timer = setTimeout(completeSubmissionFeedback, 1500);
      return () => clearTimeout(timer);
    }
  }, [snackbarOpen, completeSubmissionFeedback]);

  const isDirty = useMemo(
    () => JSON.stringify(selectedValues) !== JSON.stringify(baselineValues),
    [selectedValues, baselineValues]
  );

  const buttonDisabled = isViewMode ? true : selectedValues.completed && !isDirty;

  const handleValueChange = (name, val) => {
    const updated = {
      ...selectedValues,
      [name]: Array.isArray(val) ? [...val] : val,
      completed: false,
    };
    
    // Debug logging for string articulation techniques
    if (name === 'String Articulation Techniques') {
      console.log('String articulation techniques changed:', val);
      console.log('Updated form values:', updated);
    }
    
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: false }));
    setSelectedValues(updated);
    onChange(updated);
  };

  const renderField = field => {
    const isExtended = EXTENDED_PREFIXES.some(pref => field.name.toLowerCase().startsWith(pref));
    const val = selectedValues[field.name];

    if (
      (field.name === "music_completeness" || field.name === "music_compositional_craftsmanship") &&
      field.type === "multiple-choice"
    ) {
      return (
        <RatingSelector
          label={field.questionText}
          value={val && val.length ? parseInt(val[0], 10) : null}
          onChange={v => handleValueChange(field.name, [v.toString()])}
          mandatory={field.mandatory}
          instruction={field.instruction}
        />
      );
    }

    switch (field.type) {
      case "free-response":
        return (
          <FreeResponse
            label={field.questionText || field.name}
            mandatory={field.mandatory}
            instruction={field.instruction}
            value={val || (isExtended ? [] : "")}
            onChange={v => handleValueChange(field.name, v)}
            readOnly={field.readOnly || isViewMode}
            allowMultipleAnswers={isExtended}
            showError={Boolean(errors[field.name])}
          />
        );
      case "positive-integer":
        return (
          <PositiveIntegerInput
            label={field.questionText || field.name}
            mandatory={field.mandatory}
            maxValue={field.maxValue}
            instruction={field.instruction}
            value={val || ""}
            onChange={v => handleValueChange(field.name, v)}
          />
        );
      case "time-length":
        return (
          <TimeLengthInput
            label={field.questionText || field.name}
            mandatory={field.mandatory}
            instruction={field.instruction}
            value={val || null}
            onChange={v => handleValueChange(field.name, v)}
          />
        );
      case "multiple-choice":
        return (
          <MultipleChoice
            questionText={field.questionText}
            label={field.name}
            options={field.items}
            mandatory={field.mandatory}
            instruction={field.instruction}
            allowAnyLevelSelection={field.allowAnyLevelSelection}
            singleSelect={field.singleSelect}
            showError={Boolean(errors[field.name])}
            initialValue={[].concat(val || [])}
            onSelect={v => handleValueChange(field.name, v)}
          />
        );
      case "rating":
        return (
          <RatingSelector
            label={field.questionText}
            value={val}
            onChange={v => handleValueChange(field.name, v)}
            mandatory={field.mandatory}
            instruction={field.instruction}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="annotation-form-container">
      <Typography variant="h4" align="center" className="annotation-form-title">
        Annotation Form (标注表)
      </Typography>

      {Object.keys(filteredAttributes).length ? (
        Object.entries(filteredAttributes).map(([category, fields]) => (
          <div key={category} className="annotation-section">
            {category !== "Basic Information" && (
              <>
                <Typography variant="h6" className="section-title">
                  {category}
                </Typography>
                <Divider sx={{ mb: 3 }} />
              </>
            )}
            <Grid container spacing={3}>
              {fields.map(field => (
                <Grid key={field.name} item xs={12} sm={field.fullWidth ? 12 : 6} md={field.fullWidth ? 12 : 6}>
                  {renderField(field)}
                </Grid>
              ))}
            </Grid>
          </div>
        ))
      ) : (
        <Typography align="center">No form attributes defined.</Typography>
      )}

      {!isViewMode && (
        <Button
          variant="contained"
          fullWidth
          disabled={buttonDisabled}
          sx={{ bgcolor: "black", color: "white", '&:hover': { bgcolor: '#333' } }}
          onClick={() => {
            if (isFormValid) {
              const finalValues = { ...cloneFormValues(selectedValues), completed: true };
              
              // Validation: Prevent non-string instruments from having string articulation data
              const isCurrentTrackString = trackName ? isStringInstrument(trackName) : false;
              const hasStringArticulationData = finalValues['String Articulation Techniques'] && 
                Array.isArray(finalValues['String Articulation Techniques']) && 
                finalValues['String Articulation Techniques'].length > 0;
              
              const hasStringArticulationDataSnakeCase = finalValues['string_articulation_techniques'] && 
                Array.isArray(finalValues['string_articulation_techniques']) && 
                finalValues['string_articulation_techniques'].length > 0;
              
              if (!isCurrentTrackString && (hasStringArticulationData || hasStringArticulationDataSnakeCase)) {
                console.warn('AnnotationForm: Non-string instrument attempting to submit string articulation data - clearing it');
                console.warn('Track name:', trackName, 'Is string:', isCurrentTrackString);
                console.warn('String data found:', {
                  'String Articulation Techniques': finalValues['String Articulation Techniques'],
                  'string_articulation_techniques': finalValues['string_articulation_techniques']
                });
                
                // Clear all variations of string articulation fields
                delete finalValues['String Articulation Techniques'];
                delete finalValues['string_articulation_techniques'];
                delete finalValues['String_Articulation_Techniques'];
                delete finalValues['stringArticulationTechniques'];
                
                console.warn('AnnotationForm: String articulation data cleared from form submission');
              }
              
              setSelectedValues(finalValues);
              setBaselineValues(cloneFormValues(finalValues));
              setSnackbarOpen(true);
              onChange(finalValues);
              if (ownerId === undefined) {
                pendingLegacyValuesRef.current = cloneFormValues(finalValues);
              } else {
                submittedOwnerIdRef.current = ownerId;
                onSubmit(ownerId, cloneFormValues(finalValues));
              }
            } else {
              const newErrors = {};
              Object.values(filteredAttributes).flat().forEach(f => {
                if (f.mandatory && !isFieldValid(f.name, f.type)) newErrors[f.name] = true;
              });
              setErrors(newErrors);
            }
          }}
        >
          SUBMIT (提交)
        </Button>
      )}

      {snackbarOpen && (
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1400,
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            px: 4,
            py: 3,
            minWidth: "280px",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#2e7d32",
              borderRadius: "50%",
              width: 48,
              height: 48,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: 1,
            }}
          >
              <CheckIcon sx={{ color: "white", fontSize: 32 }} />
          </Box>

          <Typography
            sx={{
              fontSize: "1.2rem",
              fontWeight: "600",
              color: "#000",
              textAlign: "center",
              mb: 1.5,
            }}
          >
            Form Submitted!
          </Typography>
          

          <Button
            onClick={() => {
              if (submittedOwnerIdRef.current !== undefined) {
                completeSubmissionFeedback();
              } else {
                setSnackbarOpen(false);
              }
            }}
            sx={{
              position: "absolute",
              top: 6,
              right: 16,
              minWidth: "auto",
              padding: 0,
              color: "#888",
              fontSize: "1.6rem",
              lineHeight: "1",
              "&:hover": { color: "black", background: "transparent" },
            }}
          >
            ×
          </Button>
        </Box>
      )}
    </div>
  );
}
